/**
 * PMW event bus
 *
 * Native replacement for the jQuery event bus that used to carry all
 * pmw:* events (jQuery(document).on / .trigger). The library core must
 * run without jQuery on any shop platform, so all internal event traffic
 * goes through this emitter.
 *
 * Semantics:
 * - Handlers receive the payload directly: pmw.bus.on("pmw:x", (payload) => {}).
 *   (jQuery handlers received (event, payload); the migration drops the
 *   unused event argument.)
 * - Errors in one handler never break the chain: each handler runs in its
 *   own try/catch and failures are routed to pmw.console.error.
 * - Emission is synchronous, in registration order, like jQuery's.
 *
 * WordPress compatibility: customer snippets documented over the years use
 * jQuery events, both outbound (jQuery(document).on("pmw:event:purchase"))
 * and inbound (jQuery(document).trigger("pmw:add-to-cart", product)). The
 * WordPress platform layer loads the jQuery bridge which mirrors bus
 * events onto jQuery and forwards jQuery-triggered pmw:* events into the
 * bus. The core itself never touches jQuery.
 *
 * @since 1.65.0
 */

(function (pmw) {

	window.pmw = window.pmw || {};

	/** @type {Map<string, Array<Function>>} */
	const listeners = new Map();

	/**
	 * Callbacks the bridge (or tests) can register to observe every emit,
	 * e.g. to mirror events onto jQuery for legacy snippets.
	 *
	 * @type {Array<Function>}
	 */
	const emitObservers = [];

	/**
	 * Replayable events.
	 *
	 * The purchase event fires exactly once per order and is then locked out
	 * permanently: the order ID is written to storage immediately after the
	 * emit, so it never fires again, not even on a page reload. A third-party
	 * integration that registers its handler after an await - loading its own
	 * script, waiting on a CMP - therefore missed the order forever and the
	 * conversion was lost silently.
	 *
	 * For these events the last payload is retained and delivered to handlers
	 * that register after the emit, so registration timing stops mattering.
	 *
	 * Only page-scoped facts belong here. Interaction events (add-to-cart,
	 * select-item) must NOT be replayable: delivering a stale interaction to a
	 * late handler would make integrations report an event that already passed.
	 *
	 * @since 1.65.0
	 * @type {Set<string>}
	 */
	const replayableEvents = new Set([
		"pmw:purchase",
		"pmw:event:purchase",
	]);

	/**
	 * Last payload of each replayable event that has already been emitted.
	 *
	 * @type {Map<string, *>}
	 */
	const replayPayloads = new Map();

	/**
	 * Run a handler without letting it break the caller.
	 *
	 * @param {Function} handler
	 * @param {*} payload
	 * @param {string} context - what to name in the log if it throws
	 * @returns {void}
	 */
	const runHandler = (handler, payload, context) => {
		try {
			handler(payload);
		} catch (e) {
			(pmw.console && pmw.console.error ? pmw.console.error : console.error)(
				`PMW bus: ${context} threw`, e,
			);
		}
	};

	/**
	 * Entry events: the pmw:* events a third party dispatches INTO the
	 * library to report an interaction the library cannot detect on its own
	 * (documented in "JavaScript events").
	 *
	 * Reporting one is only useful once the library can act on it, and that
	 * moment comes late in main(): the platform's entry-event listeners are
	 * registered after the jQuery wait, the data layer wait, the awaited
	 * consent chunk and the command queue, and the pixels register themselves
	 * after that again. An entry event dispatched earlier hit an empty
	 * listener list, or listeners with not a single pixel behind them, and
	 * vanished without a trace. That included anything dispatched from inside
	 * _pmwq - the wrapper the docs recommend precisely to make load order a
	 * non-issue, which runs one require() before the listeners exist - and the
	 * library's own page-load shipping-method scan, which runs on jQuery's
	 * ready callback while the pixels are still being imported.
	 *
	 * So entry events emitted during startup are held, and emitted again
	 * through the normal path once the library is operational
	 * (pmw.bus.flushEntryEvents(), called by main() right after pmwLoad, when
	 * the listeners are registered, the pixels are live and the library's own
	 * pmwLoad-driven events have gone out).
	 *
	 * This is not the replay above: a held emit is delivered once, to whoever
	 * is listening when the flush runs. Handlers registering later still get
	 * nothing, which keeps stale interactions out of late integrations.
	 *
	 * @since 1.65.2
	 * @type {Set<string>}
	 */
	const entryEvents = new Set([
		"pmw:page-view",
		"pmw:view-item",
		"pmw:view-item-list",
		"pmw:view-category",
		"pmw:select-item",
		"pmw:search",
		"pmw:add-to-cart",
		"pmw:remove-from-cart",
		"pmw:view-cart",
		"pmw:add-to-wishlist",
		"pmw:begin-checkout",
		"pmw:add-shipping-info",
		"pmw:add-payment-info",
		"pmw:place-order",
		"pmw:purchase",
		"pmw:login",
		"pmw:account-created",
	]);

	/**
	 * Entry-event emits received during startup, in dispatch order across all
	 * event names: a snippet that reports an add-to-cart and then a checkout
	 * start must not have them replayed the other way round.
	 *
	 * @type {Array<{eventName: string, payload: *}>}
	 */
	const pendingEntryEmits = [];

	/**
	 * Cap on the buffer. A page that dispatches entry events in a loop before
	 * the library is up is broken either way; the cap keeps the buffer from
	 * growing without bound.
	 *
	 * @type {number}
	 */
	const maxPendingEntryEmits = 20;

	/**
	 * Whether entry events are still being held. True until the library is
	 * operational, false for the rest of the page's life.
	 *
	 * @type {boolean}
	 */
	let bufferingEntryEmits = true;

	/**
	 * Hold an entry event that arrived while the library was still starting
	 * up.
	 *
	 * @param {string} eventName
	 * @param {*} payload
	 * @returns {boolean} whether the emit was held instead of delivered
	 */
	const bufferEntryEmit = (eventName, payload) => {

		if (!bufferingEntryEmits) return false;
		if (!entryEvents.has(eventName)) return false;

		if (pendingEntryEmits.length >= maxPendingEntryEmits) {
			(pmw.console && pmw.console.error ? pmw.console.error : console.error)(
				`PMW bus: more than ${maxPendingEntryEmits} entry events arrived while the library was still loading, ${eventName} dropped`,
			);
			return true;
		}

		pendingEntryEmits.push({eventName, payload});

		if (pmw.console && pmw.console.log) {
			pmw.console.log(`${eventName} arrived while the library was still loading, held until it is ready`);
		}

		return true;
	};

	pmw.bus = {

		/**
		 * Register a handler for an event.
		 *
		 * For replayable events (see replayableEvents) that have already been
		 * emitted, the handler receives the retained payload immediately and
		 * synchronously, once. Registering late is therefore safe.
		 *
		 * @param {string} eventName
		 * @param {Function} handler - receives (payload)
		 * @returns {void}
		 */
		on(eventName, handler) {
			if (typeof handler !== "function") return;
			if (!listeners.has(eventName)) listeners.set(eventName, []);
			listeners.get(eventName).push(handler);

			// Replay after registration, so a handler that removes itself
			// (bus.once) or registers further handlers behaves as it would
			// have on the original emit.
			if (replayPayloads.has(eventName)) {
				runHandler(handler, replayPayloads.get(eventName), `replayed handler for ${eventName}`);
			}
		},

		/**
		 * Remove a previously registered handler.
		 *
		 * @param {string} eventName
		 * @param {Function} handler
		 * @returns {void}
		 */
		off(eventName, handler) {
			const handlers = listeners.get(eventName);
			if (!handlers) return;
			const index = handlers.indexOf(handler);
			if (index !== -1) handlers.splice(index, 1);
		},

		/**
		 * Register a handler that runs at most once.
		 *
		 * @param {string} eventName
		 * @param {Function} handler
		 * @returns {void}
		 */
		once(eventName, handler) {
			const wrapper = (payload) => {
				pmw.bus.off(eventName, wrapper);
				handler(payload);
			};
			pmw.bus.on(eventName, wrapper);
		},

		/**
		 * Emit an event to all handlers, synchronously, in registration
		 * order. A throwing handler is logged and skipped, never fatal.
		 *
		 * @param {string} eventName
		 * @param {*} [payload]
		 * @returns {void}
		 */
		emit(eventName, payload) {

			// pmwLoad marks the end of startup: the library's own page-load
			// events are emitted from its pmwLoad handlers and must not be
			// held. What is already held is delivered by main() right after
			// those handlers ran, because they set up state the held events
			// need (the cart, the click IDs).
			if ("pmwLoad" === eventName) bufferingEntryEmits = false;

			// Entry events that arrive during startup are held, not dropped.
			// Nothing else may happen for them here: the outbound jQuery
			// mirror and the replay retention run when the held emit is
			// actually delivered, in flushEntryEvents().
			if (bufferEntryEmit(eventName, payload)) return;

			// Retain before dispatch, so a handler that registers another
			// handler for the same replayable event still sees the payload.
			if (replayableEvents.has(eventName)) replayPayloads.set(eventName, payload);

			const handlers = listeners.get(eventName);
			if (handlers) {
				// Copy: handlers may add/remove listeners while running.
				for (const handler of Array.from(handlers)) {
					runHandler(handler, payload, `handler for ${eventName}`);
				}
			}

			for (const observer of emitObservers) {
				try {
					observer(eventName, payload);
				} catch (e) {
					(pmw.console && pmw.console.error ? pmw.console.error : console.error)(
						"PMW bus: emit observer threw", e,
					);
				}
			}
		},

		/**
		 * Observe every emit (used by the WordPress jQuery bridge to mirror
		 * events for legacy snippets, and by tests).
		 *
		 * @param {Function} observer - receives (eventName, payload)
		 * @returns {void}
		 */
		observeEmits(observer) {
			if (typeof observer === "function") emitObservers.push(observer);
		},

		/**
		 * Whether any handler is registered for an event. Lets hot paths
		 * skip payload construction when nobody listens.
		 *
		 * @param {string} eventName
		 * @returns {boolean}
		 */
		hasListeners(eventName) {
			return (listeners.get(eventName) || []).length > 0;
		},

		/**
		 * Stop holding entry events and deliver everything that was held, in
		 * dispatch order, through the normal emit path.
		 *
		 * Called by main() once the library is operational: listeners
		 * registered, pixels loaded and initialized. Idempotent, and a no-op
		 * when nothing was held.
		 *
		 * @since 1.65.2
		 * @returns {void}
		 */
		flushEntryEvents() {

			bufferingEntryEmits = false;

			if (0 === pendingEntryEmits.length) return;

			// Splice out first: emitting can dispatch further entry events
			// (a handler reacting to an add-to-cart), and those must go out
			// normally instead of landing back in the buffer.
			const held = pendingEntryEmits.splice(0, pendingEntryEmits.length);

			if (pmw.console && pmw.console.log) {
				pmw.console.log(`delivering ${held.length} entry event(s) that arrived while the library was still loading`);
			}

			for (const {eventName, payload} of held) {
				pmw.bus.emit(eventName, payload);
			}
		},

		/**
		 * The entry events the library accepts from third parties. The
		 * WordPress jQuery bridge reads this to accept the same events from
		 * jQuery triggers before the library has subscribed to them.
		 *
		 * @since 1.65.2
		 * @returns {string[]}
		 */
		entryEventNames() {
			return Array.from(entryEvents);
		},
	};

}(window.pmw = window.pmw || {}));
